// Outside publishers: the registry and the weekly bill live on the finance
// side; the Share tab only needs the dropdown and the app's own assignment,
// which sit on the apps-setup router so the ASO's section gate covers them.
import { api } from './client';

export interface Publisher {
  id: string;
  name: string;
  chat_id: string | null;
  thread_id: number | null;
  publish_cents: number;
  rent_cents: number;
  enabled: number;
  contact: string | null;
  note: string | null;
  created_at: string;
  app_count: number;
  live_count: number;
}

export interface PublisherOption {
  id: string;
  name: string;
  publish_cents: number;
  rent_cents: number;
  has_chat: boolean;
}

export interface AppPublisher {
  publisher_id: string | null;
  publisher_name: string | null;
  publish_cents: number | null;
  rent_cents: number | null;
  rent_from_week: string | null;   // Monday the current rate started on, when it was changed after assignment
  assigned_at: string | null;
  released_at: string | null;
  published_at: string | null;
  listing_status: ListingStatus | null;
  listing_checked_at: string | null;
}

export type ListingStatus = 'pending' | 'ok' | 'issues' | 'gave_up';
export type LocaleIssue =
  | 'not_available' | 'fetch_error' | 'locale_missing' | 'title_mismatch' | 'short_mismatch' | 'screenshot_count';

export interface LocaleResult {
  lang: string;
  ok: boolean;
  issues: LocaleIssue[];
  store: { title: string | null; short_description: string | null; screenshots: number | null };
  expected: { title: string; short_description: string; screenshots: number };
}

export interface ListingReport {
  checked_at: string;
  package_name: string;
  locales: LocaleResult[];
  ok: boolean;
}

export interface PublisherApp {
  id: string;
  name: string;
  bundle_id: string;
  icon_url: string | null;
  released_at: string | null;
  is_banned: number;
  banned_at: string | null;
  publish_cents: number | null;
  rent_cents: number | null;
  rent_from_week: string | null;   // Monday the current rate started on, when it was changed after assignment
  assigned_at: string | null;
  published_at: string | null;
  handoff_at: string | null;
  listing_status: ListingStatus | null;
  listing_attempts: number | null;
  listing_checked_at: string | null;
  listing_next_at: string | null;
  listing_report: ListingReport | null;
  store_developer: string | null;    // the developer account the store page shows (kept after a ban)
  store_developer_url: string | null; // that account's exact store link (Play writes invisible characters into names)
  developers_before: { url: string; name: string; last_seen_at: number | null }[]; // accounts it was listed under earlier, newest first
  publisher_account: string | null;  // typed by hand; wins over store_developer in /stats
  // Paid only once approved (listing matched, or by hand); NULL = not paid yet.
  approved_at: string | null;
  approval_source: 'auto' | 'admin' | 'bot' | 'backfill' | null;
  approved_by: string | null;
  // The outside client company the app was published for; null = ours.
  client_id: string | null;
  client_name: string | null;
}

// client_id / client_name: the outside client the app was published for; null = ours.
export interface WeekPublishItem {
  app_id: string; app_name: string; bundle_id: string; at: string; amount_cents: number;
  client_id: string | null; client_name: string | null;
  void: boolean;           // released this week and gone before Monday: not paid, not in the totals
  pending: boolean;        // not approved yet: not in the totals until it is
}
export interface WeekRentItem {
  app_id: string; app_name: string; bundle_id: string; released_at: string;
  client_id: string | null; client_name: string | null;
  rate_cents: number;      // the weekly rate on the app
  days: number;            // live days in the week (so far, for the current week)
  amount_cents: number;    // rate * days / 7
  first_week: boolean;     // the app came out this week
  pending: boolean;        // not approved yet: not in the totals until it is
}

export type WeekStatus = 'open' | 'agreed' | 'paid';

export interface PublisherWeekRow {
  publisher_id: string;
  publisher_name: string;
  enabled: boolean;
  published: WeekPublishItem[];
  rent: WeekRentItem[];
  published_cents: number;
  rent_cents: number;
  total_cents: number;
  pending_cents: number;   // what unapproved apps add once approved; not in total_cents
  status: WeekStatus;
  paid_cents: number | null;
  note: string | null;
  updated_at: string | null;
}

/** The week regrouped by client: what we pay publishers for each client's apps. client_id null = ours, listed last. */
export interface ClientWeekTotal {
  client_id: string | null;
  client_name: string | null;
  published: number;
  published_cents: number;
  rent: number;
  rent_cents: number;
  total_cents: number;
  pending_cents: number;   // unapproved apps; not in total_cents
  publishers: { publisher_id: string; publisher_name: string; total_cents: number; pending_cents: number }[];
}

export interface PublisherWeek {
  week: string;
  current: string;
  rows: PublisherWeekRow[];
  clients: ClientWeekTotal[];
}

// ---- apps-setup side ------------------------------------------------------

export const getPublisherOptions = () =>
  api.get<PublisherOption[]>('/admin/apps-setup/publishers');

export const getAppPublisher = (appId: string) =>
  api.get<AppPublisher>(`/admin/apps-setup/app-publisher/${appId}`);

export const setAppPublisher = (
  appId: string,
  input: { publisher_id: string | null; publish_cents?: number; rent_cents?: number; rent_from_week?: string },
) => api.put<{ publisher_id: string | null; publish_cents: number; rent_cents: number }>(
  `/admin/apps-setup/app-publisher/${appId}`, input,
);

// ---- finance side ---------------------------------------------------------

const BASE = '/admin/finance/publishers';

export const getPublishers = () =>
  api.get<{ publishers: Publisher[] }>(BASE);

export type PublisherInput = Partial<Pick<
  Publisher, 'name' | 'chat_id' | 'thread_id' | 'publish_cents' | 'rent_cents' | 'contact' | 'note'
>> & { enabled?: boolean };

export const createPublisher = (input: PublisherInput) => api.post<Publisher>(BASE, input);
export const updatePublisher = (id: string, input: PublisherInput) => api.patch<Publisher>(`${BASE}/${id}`, input);
export const testPublisherMessage = (id: string) => api.post<{ sent: boolean }>(`${BASE}/${id}/test-message`, {});

export const getPublisherApps = (id: string) => api.get<PublisherApp[]>(`${BASE}/${id}/apps`);
/** New weekly rent for the publisher's existing apps from `from_week` on; earlier weeks keep their rate. */
export const setPublisherRent = (
  id: string,
  input: { rent_cents: number; from_week: string; app_ids?: string[]; set_default?: boolean },
) => api.post<{ changed: number; from_week: string; rent_cents: number }>(`${BASE}/${id}/rent`, input);
export const setPublisherAppAccount = (appId: string, account: string | null) =>
  api.patch<{ account: string | null }>(`${BASE}/apps/${appId}/account`, { account });
export const approvePublisherApp = (appId: string) =>
  api.post<{ outcome: string }>(`${BASE}/apps/${appId}/approve`, {});
export const revokePublisherApproval = (appId: string) =>
  api.delete<null>(`${BASE}/apps/${appId}/approve`);
export const runPublisherListingCheck = (appId: string) =>
  api.post<ListingReport>(`${BASE}/apps/${appId}/check`, {});

export const getPublisherWeek = (week: string) => api.get<PublisherWeek>(`${BASE}/weeks/${week}`);
export const updatePublisherWeek = (
  week: string, publisherId: string,
  input: { status?: WeekStatus; paid_cents?: number | null; note?: string | null },
) => api.patch<unknown>(`${BASE}/weeks/${week}/${publisherId}`, input);

/** Revenue vs what the publisher costs us, per app per week since release. Dashboard only. */
export interface AppEconomicsWeek {
  week: string;            // Monday
  revenue_cents: number;   // payout on clicks that came through the app (by click day)
  deps: number;
  cost_cents: number;      // publish fee + rent, exactly as billed
}
export interface AppEconomics {
  app_id: string;
  app_name: string;
  bundle_id: string;
  publisher_id: string;
  client_id: string | null;    // the outside client it was published for; null = ours
  client_name: string | null;
  released_at: string;
  is_banned: boolean;
  banned_at: string | null;
  weeks: AppEconomicsWeek[];   // release week first
}
export const getPublisherEconomics = () =>
  api.get<{ current: string; apps: AppEconomics[] }>(`${BASE}/economics`);
